import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { CheckCircle2, ShieldCheck, Eye, EyeOff, AlertTriangle, Loader2 } from "lucide-react";

/**
 * /restablecer-contrasena
 *
 * Al montar valida el token contra el backend (estados: valid | expired | used | invalid).
 * Muestra el formulario sólo si el token es válido. En los demás casos ofrece pedir otro enlace.
 */

const MIN_LEN = 12;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";

  const [tokenState, setTokenState] = useState("checking"); // checking | valid | expired | used | invalid
  const [ttl, setTtl] = useState(null);

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [status, setStatus] = useState("idle");             // idle | loading | done
  const [error, setError] = useState("");

  // Referrer-Policy: no-referrer (defensa en profundidad para evitar filtrar el token)
  useEffect(() => {
    let meta = document.querySelector('meta[name="referrer"]');
    const created = !meta;
    if (created) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "referrer");
      document.head.appendChild(meta);
    }
    const prev = meta.getAttribute("content");
    meta.setAttribute("content", "no-referrer");
    return () => {
      if (prev != null) meta.setAttribute("content", prev);
      else if (created) meta.remove();
    };
  }, []);

  // Validar el token una vez al cargar la página
  useEffect(() => {
    if (!token) { setTokenState("invalid"); return; }
    api.get(`/auth/reset-token/${encodeURIComponent(token)}`)
      .then(({ data }) => {
        setTokenState(data.status || "invalid");
        setTtl(data.ttl_minutes_remaining ?? null);
      })
      .catch(() => setTokenState("invalid"));
  }, [token]);

  // Requisitos derivados de la contraseña
  const req = useMemo(() => ({
    length: pwd.length >= MIN_LEN,
    match: pwd.length > 0 && pwd === confirm,
  }), [pwd, confirm]);
  const canSubmit = req.length && req.match && status !== "loading";

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setStatus("loading");
    try {
      await api.post("/auth/reset-password", {
        token,
        password: pwd,
        password_confirmation: confirm,
      });
      setStatus("done");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setStatus("idle");
    }
  };

  // ─── Vista principal según estado del token ───────────────────
  if (tokenState === "checking") {
    return (
      <div className="container-app py-24 text-center">
        <Loader2 size={28} className="mx-auto animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground mt-3">Verificando enlace…</p>
      </div>
    );
  }

  if (tokenState !== "valid" && status !== "done") {
    return <InvalidTokenScreen state={tokenState} />;
  }

  return (
    <div className="container-app py-16 grid lg:grid-cols-2 gap-16 items-center">
      <div className="hidden lg:block">
        <div className="label-eyebrow">Seguridad</div>
        <h1 className="serif text-5xl mt-3 leading-tight">Crea una nueva contraseña.</h1>
        <p className="mt-6 text-muted-foreground max-w-md flex items-start gap-2">
          <ShieldCheck size={18} className="text-[hsl(var(--accent))] mt-1 shrink-0" />
          <span>
            Usa al menos <strong className="text-foreground">{MIN_LEN} caracteres</strong>.
            Puedes utilizar una frase larga y fácil de recordar.
          </span>
        </p>
        {ttl != null && (
          <p className="mt-4 text-xs text-muted-foreground">
            Este enlace estará disponible durante {ttl} minuto{ttl === 1 ? "" : "s"} más.
          </p>
        )}
      </div>

      <form onSubmit={submit} className="panel p-10 max-w-md w-full mx-auto" data-testid="reset-form" noValidate>
        {status === "done" ? (
          <div className="text-center py-4" data-testid="reset-success">
            <CheckCircle2 size={48} className="mx-auto text-[hsl(var(--accent))]" />
            <h2 className="serif text-3xl mt-4">¡Contraseña actualizada!</h2>
            <p className="text-sm text-muted-foreground mt-3">
              Tu contraseña fue actualizada correctamente. Ya puedes iniciar sesión.
            </p>
            <button
              type="button"
              onClick={() => nav("/login")}
              className="btn-primary mt-6 w-full"
              data-testid="reset-go-login"
            >
              Iniciar sesión
            </button>
          </div>
        ) : (
          <>
            <h2 className="serif text-3xl">Crea una nueva contraseña</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Usa al menos {MIN_LEN} caracteres. Puedes utilizar una frase larga y fácil de recordar.
            </p>
            <div className="mt-8 space-y-4">
              <div>
                <label htmlFor="new-password" className="label-eyebrow">Nueva contraseña</label>
                <div className="relative mt-1.5">
                  <input
                    id="new-password"
                    name="password"
                    type={showPwd ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    minLength={MIN_LEN}
                    maxLength={128}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    className="field pr-10 w-full"
                    data-testid="reset-pwd"
                    disabled={status === "loading"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                    aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
                    data-testid="reset-pwd-toggle"
                  >
                    {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {pwd.length > 0 && (
                  <div className={`text-xs mt-1.5 ${req.length ? "text-emerald-300" : "text-amber-300"}`}>
                    {req.length ? "✓" : "•"} Al menos {MIN_LEN} caracteres ({pwd.length}/{MIN_LEN})
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="new-password-confirm" className="label-eyebrow">Confirmar nueva contraseña</label>
                <input
                  id="new-password-confirm"
                  name="password_confirmation"
                  type={showPwd ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  minLength={MIN_LEN}
                  maxLength={128}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="field mt-1.5"
                  data-testid="reset-confirm"
                  disabled={status === "loading"}
                />
                {confirm.length > 0 && (
                  <div className={`text-xs mt-1.5 ${req.match ? "text-emerald-300" : "text-amber-300"}`}>
                    {req.match ? "✓ Las contraseñas coinciden" : "• Las contraseñas aún no coinciden"}
                  </div>
                )}
              </div>

              {error && (
                <div className="text-sm text-[hsl(var(--destructive))]" data-testid="reset-error" role="alert">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                className="btn-primary w-full disabled:opacity-60"
                data-testid="reset-submit"
              >
                {status === "loading" ? "Guardando…" : "Guardar nueva contraseña"}
              </button>

              <div className="text-sm text-muted-foreground text-center">
                <Link to="/login" className="link-underline text-foreground">Volver a iniciar sesión</Link>
              </div>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

function InvalidTokenScreen({ state }) {
  const copy = {
    expired: {
      title: "Enlace vencido",
      body: "Este enlace ha vencido. Solicita uno nuevo.",
    },
    used: {
      title: "Enlace ya utilizado",
      body: "Este enlace ya fue utilizado. Solicita uno nuevo si aún necesitas cambiar tu contraseña.",
    },
    invalid: {
      title: "Enlace no válido",
      body: "El enlace no es válido o ha vencido.",
    },
  }[state] || {
    title: "Enlace no válido",
    body: "El enlace no es válido o ha vencido.",
  };

  return (
    <div className="container-app py-24 max-w-md mx-auto text-center" data-testid={`reset-${state}`}>
      <div className="panel p-10">
        <AlertTriangle size={44} className="mx-auto text-amber-300" />
        <h2 className="serif text-3xl mt-4">{copy.title}</h2>
        <p className="text-sm text-muted-foreground mt-3">{copy.body}</p>
        <div className="mt-6 flex flex-col gap-2">
          <Link to="/olvide-mi-contrasena" className="btn-primary w-full" data-testid="reset-request-new">
            Solicitar nuevo enlace
          </Link>
          <Link to="/login" className="btn-ghost w-full">Volver a iniciar sesión</Link>
        </div>
      </div>
    </div>
  );
}
